import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';

function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      
      const response = await adminAPI.getAllContacts(params);
      setContacts(response.data.contacts);
      setTotalPages(response.data.pagination.pages);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (contactId, newStatus) => {
    try {
      await adminAPI.updateContactStatus(contactId, { status: newStatus });
      fetchContacts();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update contact status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-700';
      case 'in_progress': return 'bg-yellow-100 text-yellow-700';
      case 'resolved': return 'bg-green-100 text-green-700';
      case 'closed': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900">Contact Submissions</h1>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none"
        >
          <option value="">All Statuses</option>
          <option value="new">New</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : (
        <>
          <div className="space-y-4">
            {contacts.map((contact) => (
              <div key={contact._id} className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">{contact.subject}</h3>
                    <p className="text-sm text-gray-600">
                      From: <span className="font-semibold">{contact.name}</span> ({contact.email})
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(contact.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(contact.status)}`}>
                    {contact.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                
                <p className="text-gray-700 mb-4 whitespace-pre-wrap">{contact.message}</p>

                {contact.adminNotes && (
                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <p className="text-sm font-semibold text-gray-700 mb-1">Admin Notes:</p>
                    <p className="text-sm text-gray-600">{contact.adminNotes}</p>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <select
                    value={contact.status}
                    onChange={(e) => handleStatusUpdate(contact._id, e.target.value)}
                    className="px-3 py-2 border-2 border-gray-200 rounded-lg text-sm font-semibold focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none"
                  >
                    <option value="new">New</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                  <a
                    href={`mailto:${contact.email}?subject=Re: ${contact.subject}`}
                    className="px-4 py-2 bg-pink-500 text-white rounded-lg text-sm font-semibold hover:bg-pink-600 transition-colors"
                  >
                    Reply
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex justify-center items-center gap-4 mt-6">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 bg-white border-2 border-gray-200 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:border-pink-500 transition-colors"
            >
              Previous
            </button>
            <span className="text-gray-700 font-semibold">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 bg-white border-2 border-gray-200 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:border-pink-500 transition-colors"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default Contacts;

